import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * 새로운 연구소 - 민트톤 통일 랜딩 페이지
 * - 앱 내 라우팅(/login, /signup)으로 자연스럽게 연결
 * - 섹션 스크롤(#services, #process, #faq) 지원
 */
// FAQ 아이템 (접힘/펼침)
const FaqItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <button
        className="flex w-full items-center justify-between px-5 py-4 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-sm font-semibold text-slate-800">{question}</span>
        <svg
          width="18" height="18" viewBox="0 0 24 24" fill="none"
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path d="M6 9l6 6 6-6" stroke="#64748b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-5 pb-4 pt-3">
          <p className="text-sm leading-relaxed text-slate-500">{answer}</p>
        </div>
      )}
    </div>
  );
};

const LandingPage: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const year = useMemo(() => new Date().getFullYear(), []);
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const userRole = useMemo(() => {
    try {
      const raw =
        localStorage.getItem("auth_user") ||
        sessionStorage.getItem("auth_user");
      if (!raw) return null;
      return (JSON.parse(raw) as { role?: string }).role ?? null;
    } catch {
      return null;
    }
  }, []);

  const isStudent = userRole === "student";
  const startPath = isStudent ? "/student" : "/teacher";

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  if (isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="h-14 w-14 overflow-hidden rounded-2xl shadow-sm ring-1 ring-slate-200">
            <img src="/logo.png" alt="새로운 연구소 로고" className="h-full w-full object-cover" />
          </div>
          <p className="text-base font-semibold text-slate-700">새로운 연구소</p>
          <div className="mt-2 flex gap-3">
            <button
              onClick={handleLogout}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 shadow-sm hover:bg-slate-50 transition-colors"
            >
              로그아웃
            </button>
            <Link
              to={startPath}
              className="rounded-2xl bg-gradient-to-r from-[#57c4c6] to-[#2aa7aa] px-5 py-2.5 text-sm font-semibold text-white shadow hover:opacity-90 transition-opacity"
            >
              시작하기
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Background accents (mint unified) */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-44 left-1/2 h-[560px] w-[920px] -translate-x-1/2 rounded-full bg-gradient-to-r from-teal-500/25 via-cyan-400/10 to-emerald-400/18 blur-3xl" />
        <div className="absolute -bottom-60 right-[-160px] h-[560px] w-[560px] rounded-full bg-gradient-to-r from-teal-500/18 to-cyan-400/14 blur-3xl" />
        <div className="absolute top-28 left-[-180px] h-[420px] w-[420px] rounded-full bg-gradient-to-r from-emerald-400/12 to-teal-500/12 blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-200/60 bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <a href="#top" className="flex items-center gap-3" onClick={closeMobile}>
            <div className="h-9 w-9 overflow-hidden rounded-2xl bg-slate-900/5 shadow-sm ring-1 ring-slate-200">
              {/* public/logo.png */}
              <img src="/logo.png" alt="새로운 연구소 로고" className="h-full w-full object-cover" />
            </div>
            <span className="text-lg font-semibold tracking-tight">새로운 연구소</span>
          </a>

          <nav className="hidden items-center gap-6 md:flex">
            <a className="text-sm font-medium text-slate-700 hover:text-slate-900" href="#services">
              Service
            </a>
            <a className="text-sm font-medium text-slate-700 hover:text-slate-900" href="#process">
              Process
            </a>
            <a className="text-sm font-medium text-slate-700 hover:text-slate-900" href="#faq">
              FAQ
            </a>
          </nav>

          {/* ✅ Header CTA: 로그인 상태면 바로 사용, 아니면 회원가입/로그인 */}
          <div className="hidden items-center gap-2 md:flex">
            {isAuthenticated ? (
              <>
                {!isStudent && (
                  <Link
                    to="/analysis"
                    className="rounded-2xl px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    분석 히스토리
                  </Link>
                )}
                <Link
                  to={isStudent ? "/student" : "/exam-upload"}
                  className="rounded-2xl bg-gradient-to-r from-[#57c4c6] to-[#2aa7aa] px-4 py-2 text-sm font-semibold text-white shadow hover:opacity-90 transition-opacity"
                >
                  {isStudent ? "시작하기" : "시험 업로드"}
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/signup"
                  className="rounded-2xl px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                >
                  회원가입
                </Link>
                <Link
                  to="/login"
                  className="rounded-2xl bg-gradient-to-r from-[#57c4c6] to-[#2aa7aa] px-4 py-2 text-sm font-semibold text-white shadow hover:opacity-90 transition-opacity"
                >
                  로그인
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="메뉴 열기"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 7h14M5 12h14M5 17h14" stroke="#0f172a" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        <div className={`md:hidden ${mobileOpen ? "block" : "hidden"} border-t border-slate-200/60 bg-white/90 backdrop-blur`}>
          <div className="mx-auto max-w-6xl px-4 py-3">
            <div className="grid gap-2">
              <a className="rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100" href="#services" onClick={closeMobile}>
                Service
              </a>
              <a className="rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100" href="#process" onClick={closeMobile}>
                Process
              </a>
              <a className="rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100" href="#faq" onClick={closeMobile}>
                FAQ
              </a>
            </div>

            {/* ✅ Mobile CTA */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              {isAuthenticated ? (
                <>
                  {!isStudent && (
                    <Link
                      to="/analysis"
                      onClick={closeMobile}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
                    >
                      분석 히스토리
                    </Link>
                  )}
                  <Link
                    to={isStudent ? "/student" : "/exam-upload"}
                    onClick={closeMobile}
                    className="rounded-2xl bg-gradient-to-r from-[#57c4c6] to-[#2aa7aa] px-4 py-3 text-center text-sm font-semibold text-white shadow hover:opacity-90 transition-opacity"
                  >
                    {isStudent ? "시작하기" : "시험 업로드"}
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/signup"
                    onClick={closeMobile}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
                  >
                    회원가입
                  </Link>
                  <Link
                    to="/login"
                    onClick={closeMobile}
                    className="rounded-2xl bg-gradient-to-r from-[#57c4c6] to-[#2aa7aa] px-4 py-3 text-center text-sm font-semibold text-white shadow hover:opacity-90 transition-opacity"
                  >
                    로그인
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main id="top">
        {/* Hero */}
        <section className="mx-auto max-w-6xl px-4 pb-8 pt-10 md:pt-14">
          <div className="grid items-center gap-8 md:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-800">
                <span className="inline-block h-2 w-2 rounded-full bg-teal-600" />
                데이터 기반 학습 설계 · 자동 리포트
              </div>

              <h1 className="mt-4 text-3xl font-bold tracking-tight md:text-5xl">
                성적이 오르는 건
                <span className="block bg-gradient-to-r from-teal-600 via-cyan-600 to-emerald-600 bg-clip-text text-transparent">
                  우연이 아니라 설계
                </span>
                입니다.
              </h1>

              <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
                새로운 연구소는 국·영·수 수업과 입시 컨설팅을 <b className="text-slate-800">하나의 학습 시스템</b>으로 묶습니다.
                학습 과정은 기록되고, 결과는 <b className="text-slate-800">명확한 보고서</b>로 증명됩니다.
              </p>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                {/* ✅ "서비스 보기"는 내부 섹션 유지 */}
                <a
                  href="#services"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#57c4c6] to-[#2aa7aa] px-5 py-3 text-sm font-semibold text-white shadow hover:opacity-90 transition-opacity"
                >
                  서비스 구성 보기
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 12h12" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M13 6l6 6-6 6" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>

                {/* ✅ 요청 반영: "진행 방식" 버튼은 로그인으로 */}
                <Link
                  to={isAuthenticated ? "/exam-upload" : "/login"}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
                >
                  {isAuthenticated ? "바로 시작하기" : "로그인으로 시작"}
                </Link>
              </div>

              <div className="mt-7 grid grid-cols-3 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  <div className="text-xs text-slate-500">학습 설계</div>
                  <div className="mt-1 text-sm font-semibold">목표·약점 기반</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  <div className="text-xs text-slate-500">피드백</div>
                  <div className="mt-1 text-sm font-semibold">문항 단위</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  <div className="text-xs text-slate-500">리포트</div>
                  <div className="mt-1 text-sm font-semibold">자동 생성</div>
                </div>
              </div>
            </div>

            {/* Visual card ... 이하 너 원본 그대로 */}
            {/* (여기부터 아래 섹션들은 네 코드 그대로 두면 됨) */}
            {/* --- */}
            {/* Visual card */}
            <div className="relative">
              <div className="rounded-2xl bg-gradient-to-br from-teal-500 via-slate-200 to-cyan-400 p-[1px] shadow-lg">
                <div className="rounded-2xl bg-white p-5 md:p-7">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-800">학습 리포트 미리보기 (MVP)</div>
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">샘플</span>
                  </div>
                  {/* ... (이하 동일) */}
                </div>
              </div>
              <div className="pointer-events-none absolute -right-6 -top-6 hidden h-24 w-24 rounded-3xl bg-teal-500/15 blur-xl md:block" />
            </div>
          </div>
        </section>

        {/* ── Services ───────────────────────────────────────────── */}
        <section id="services" className="mx-auto max-w-6xl px-4 py-16">
          <div className="mb-10 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-800">
              서비스 구성
            </span>
            <h2 className="mt-4 text-2xl font-bold tracking-tight md:text-3xl">
              학습의 전 과정을 하나로
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              시험지 촬영부터 보고서 발송까지, 교사의 모든 반복 작업을 자동화합니다.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {/* 카드 1 */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="#2aa7aa" strokeWidth="1.8"/>
                  <circle cx="12" cy="13" r="3" stroke="#2aa7aa" strokeWidth="1.8"/>
                </svg>
              </div>
              <h3 className="mb-2 text-base font-bold text-slate-800">AI 시험 분석</h3>
              <p className="text-sm leading-relaxed text-slate-500">
                시험지 사진을 업로드하면 GPT-4o Vision이 문항별 정오답을 자동 판별합니다.
                수식·풀이 과정까지 포함한 수학 해설지 스타일의 피드백을 생성합니다.
              </p>
              <ul className="mt-4 space-y-1.5 text-xs text-slate-500">
                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0"/>최대 10페이지 다중 업로드</li>
                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0"/>문항별 공식·풀이 단계 자동 생성</li>
                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0"/>오답 원인 및 학습 팁 제공</li>
              </ul>
            </div>

            {/* 카드 2 */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12h6M9 16h4M7 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2h-2" stroke="#2aa7aa" strokeWidth="1.8" strokeLinecap="round"/>
                  <path d="M9 4h6v4H9V4z" stroke="#2aa7aa" strokeWidth="1.8" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="mb-2 text-base font-bold text-slate-800">교사 검토 & 보고서</h3>
              <p className="text-sm leading-relaxed text-slate-500">
                AI가 생성한 초안을 교사가 직접 검토·수정합니다.
                점수·코멘트 편집 후 검토 완료 처리하면 PDF 보고서가 자동으로 완성됩니다.
              </p>
              <ul className="mt-4 space-y-1.5 text-xs text-slate-500">
                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0"/>초안(DRAFT) → 검토(REVIEWED) → 발송(SENT)</li>
                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0"/>점수·코멘트 실시간 편집</li>
                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0"/>PDF 자동 생성 및 다운로드</li>
              </ul>
            </div>

            {/* 카드 3 */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="#2aa7aa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="mb-2 text-base font-bold text-slate-800">자동 발송</h3>
              <p className="text-sm leading-relaxed text-slate-500">
                검토가 완료된 보고서를 이메일·카카오·SMS로 학생과 학부모에게 즉시 전달합니다.
                발송 현황과 열람 여부도 대시보드에서 확인할 수 있습니다.
              </p>
              <ul className="mt-4 space-y-1.5 text-xs text-slate-500">
                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0"/>이메일 / 카카오 / SMS 다채널</li>
                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0"/>학생·학부모 동시 발송</li>
                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0"/>발송·열람 상태 실시간 추적</li>
              </ul>
            </div>
          </div>

          {/* 하단 추가 기능 */}
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3v18h18" stroke="#2aa7aa" strokeWidth="1.8" strokeLinecap="round"/>
                  <path d="M7 16l4-4 4 4 4-4" stroke="#2aa7aa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-800">성적 추이 분석</h4>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">
                  회차별 점수 변화, 취약 유형 TOP3, 반복 오답 패턴을 시각화하여
                  학생에게 맞춤 학습 방향을 제시합니다.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="#2aa7aa" strokeWidth="1.8"/>
                  <path d="M12 7v5l3 3" stroke="#2aa7aa" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-800">비동기 처리</h4>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">
                  GPT 분석은 백그라운드에서 처리되어 업로드 후 즉시 다른 작업이 가능합니다.
                  완료되면 자동으로 결과를 확인할 수 있습니다.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Process ────────────────────────────────────────────── */}
        <section id="process" className="bg-slate-100/60 py-16">
          <div className="mx-auto max-w-6xl px-4">
            <div className="mb-10 text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-800">
                진행 방식
              </span>
              <h2 className="mt-4 text-2xl font-bold tracking-tight md:text-3xl">
                5단계로 완성되는 학습 리포트
              </h2>
            </div>

            <div className="relative">
              {/* 연결선 (데스크톱) */}
              <div className="absolute left-0 right-0 top-[2.2rem] hidden h-px bg-slate-200 md:block" style={{margin: "0 10%"}} />

              <div className="grid gap-6 md:grid-cols-5">
                {[
                  { step: "01", title: "학생 등록", desc: "이름·학교·학년·연락처 입력으로 학생 프로필 생성" },
                  { step: "02", title: "시험지 업로드", desc: "시험지 사진 촬영 후 업로드 (최대 10페이지)" },
                  { step: "03", title: "AI 분석", desc: "GPT-4o Vision이 문항별 정오답·피드백 자동 생성" },
                  { step: "04", title: "교사 검토", desc: "AI 초안 확인 후 점수·코멘트 수정 및 검토 완료" },
                  { step: "05", title: "보고서 발송", desc: "이메일·카카오·SMS로 학생·학부모에게 즉시 전달" },
                ].map(({ step, title, desc }) => (
                  <div key={step} className="flex flex-col items-center text-center">
                    <div className="relative z-10 flex h-[2.75rem] w-[2.75rem] items-center justify-center rounded-full bg-gradient-to-br from-[#57c4c6] to-[#2aa7aa] text-sm font-bold text-white shadow">
                      {step}
                    </div>
                    <h4 className="mt-3 text-sm font-bold text-slate-800">{title}</h4>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ────────────────────────────────────────────────── */}
        <section id="faq" className="mx-auto max-w-3xl px-4 py-16">
          <div className="mb-10 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-800">
              FAQ
            </span>
            <h2 className="mt-4 text-2xl font-bold tracking-tight md:text-3xl">자주 묻는 질문</h2>
          </div>

          <div className="space-y-3">
            {[
              {
                q: "어떤 과목의 시험지를 분석할 수 있나요?",
                a: "현재는 수학 시험지에 최적화되어 있습니다. GPT-4o Vision이 수식과 풀이 과정을 인식하여 문항별 해설을 자동 생성합니다.",
              },
              {
                q: "시험지가 여러 페이지일 때도 되나요?",
                a: "네, 최대 10페이지를 한 번에 업로드할 수 있습니다. 여러 장을 업로드하면 하나의 시험지로 인식하여 통합 분석합니다.",
              },
              {
                q: "AI가 생성한 내용이 잘못되면 어떻게 하나요?",
                a: "교사 검토 단계에서 AI 초안을 직접 수정할 수 있습니다. 점수와 코멘트를 편집한 뒤 검토 완료 처리하면 보고서에 반영됩니다.",
              },
              {
                q: "보고서는 어떤 방식으로 발송되나요?",
                a: "이메일, 카카오톡, SMS 중 설정된 채널로 학생과 학부모에게 동시 발송됩니다. 발송 및 열람 여부는 대시보드에서 확인할 수 있습니다.",
              },
              {
                q: "학생 데이터는 안전하게 보호되나요?",
                a: "모든 API는 JWT 인증으로 보호되며, 교사는 자신이 담당하는 학생 데이터에만 접근할 수 있습니다. 파일 업로드 시 7단계 보안 검증을 거칩니다.",
              },
            ].map(({ q, a }, i) => (
              <FaqItem key={i} question={q} answer={a} />
            ))}
          </div>
        </section>

        {/* ── CTA Banner ─────────────────────────────────────────── */}
        <section className="bg-gradient-to-r from-[#57c4c6] to-[#2aa7aa] py-14">
          <div className="mx-auto max-w-3xl px-4 text-center">
            <h2 className="text-2xl font-bold text-white md:text-3xl">
              지금 바로 시작해보세요
            </h2>
            <p className="mt-3 text-sm text-teal-50">
              시험지 한 장으로 학습 리포트가 완성됩니다.
            </p>
            <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link
                to="/signup"
                className="rounded-2xl bg-white px-6 py-3 text-sm font-semibold text-teal-700 shadow hover:bg-teal-50 transition-colors"
              >
                무료로 시작하기
              </Link>
              <Link
                to="/login"
                className="rounded-2xl border border-white/40 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                로그인
              </Link>
              <Link
                to="/kiosk"
                className="rounded-2xl border border-white/40 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                키오스크
              </Link>
            </div>
          </div>
        </section>

        <footer className="border-t border-slate-200 bg-white/60">
          <div className="mx-auto max-w-6xl px-4 py-8">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-slate-600">© {year} 새로운 연구소. All rights reserved.</p>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <Link to="/terms" className="hover:text-slate-700 hover:underline">이용약관</Link>
                <Link to="/privacy" className="hover:text-slate-700 hover:underline font-semibold">개인정보처리방침</Link>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;
